
import React from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { Card, CardContent } from '@/components/ui/card';
import { ThinkingOrbLoader } from '@/components/ui/ThinkingOrbLoader';

/**
 * Componente de Proteção de Rotas
 * 
 * Protege rotas que requerem autenticação. Se o usuário não estiver
 * logado, redireciona para a página de login preservando a URL
 * de origem para redirecionamento após o login.
 * 
 * Props:
 * - children: componentes filhos que serão renderizados se autenticado
 * 
 * Durante o carregamento do estado de autenticação, exibe ThinkingOrb
 * (state solving) em vez do spinner clássico.
 */

interface ProtectedRouteProps {
  children: React.ReactNode;
}

export const ProtectedRoute = ({ children }: ProtectedRouteProps) => {
  const { user, loading, initialAuthChecked } = useAuth();
  const location = useLocation();

  // Aguarda sessão/auth real antes de decidir login vs conteúdo
  if (loading || !initialAuthChecked) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <Card className="w-full max-w-md">
          <CardContent className="flex flex-col items-center justify-center p-8">
            <ThinkingOrbLoader state="solving" size={64} label="Verificando autenticação..." />
          </CardContent>
        </Card>
      </div>
    );
  }

  // Se não estiver autenticado, redirecionar para login
  if (!user) {
    return <Navigate to="/login" state={{ from: location }} replace />;
  }

  // Se estiver autenticado, renderizar o conteúdo
  return <>{children}</>;
};
